import { useOnValueChange } from '@calendly/hooks'
import { useCallback, useEffect, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useDisableScrollRestoration, useInitialScroll, useScrollPosition, useVisibleDateGroups } from 'components/meetings/hooks'
import { ScrollToTodayButton } from 'components/meetings/scroll_to_today_button'
import { trackContactInteraction } from 'features/contacts'
import { useTrack } from 'hooks/analytics_events'
import { usePackageNameMap } from 'hooks/use_package_name_map'
import { timezoneSelector } from 'selectors/current_user'
import { getMeetingsMetadata, scrollToToday } from 'shared/utils'


import { Bookend } from './bookend'
import { MeetingGroup } from './meeting_group'
import { Container, BottomPadding } from './styles'

/** Fallback when card’s scroll-margin-top cannot be read (matches --meeting-group-scroll-offset default) */
const DEFAULT_VIEWPORT_TOP_PX = 164

function scrollMeetingCardIntoView(cardId, containerRef) {
  const container = containerRef?.current
  const el = container?.querySelector(`[data-testid="meeting-card-${cardId}"]`)
  if (!el?.scrollIntoView) return
  const viewportTopPx = parseInt(getComputedStyle(el).scrollMarginTop, 10) || DEFAULT_VIEWPORT_TOP_PX
  const rect = el.getBoundingClientRect()
  const topNotInView = rect.top < viewportTopPx || rect.top >= window.innerHeight
  const bottomNotInView = rect.bottom > window.innerHeight
  let block = 'nearest'
  if (topNotInView || bottomNotInView) block = 'center'
  el.scrollIntoView({ block, behavior: 'smooth' })
}

export const MeetingContainer = ({
  meetings,
  onLoadPrevious,
  onLoadNext,
  showBuffers,
  selectedMeetingId,
  hasPrevious,
  hasNext,
  isNextLoading,
  isPreviousLoading,
  contactUuid = null,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  firstUpcomingEventKey,
  onVisibleDateRangeChange
}) => {
  useDisableScrollRestoration()
  const containerRef = useRef(null)
  const timezone = useSelector(timezoneSelector)

  const meetingsArray = Array.isArray(meetings) ? meetings : []
  const { dateRange, registerDateGroupRef } = useVisibleDateGroups(
    containerRef,
    meetingsArray,
    200
  )

  useEffect(() => {
    if (onVisibleDateRangeChange) {
      onVisibleDateRangeChange(dateRange)
    }
  }, [dateRange, onVisibleDateRangeChange])
  const dataLoadedRef = useRef(false)
  const meetingsSignatureRef = useRef(null)
  const track = useTrack()

  const setScrollData = useScrollPosition(meetings, (scrollData) => {
    if (typeof window === 'undefined') return

    const heightDiff = document.documentElement.scrollHeight - scrollData.height
    window.scrollTo(0, scrollData.top + heightDiff)
  })

  useInitialScroll(containerRef, meetings, timezone)

  // Only scroll to today when the list data actually changed (e.g. initial load), not when
  // the reference changed due to selection (URL update) which would jump the list.
  useOnValueChange(meetings, () => {
    if (dataLoadedRef.current || selectedMeetingId) return
    const arr = Array.isArray(meetings) ? meetings : []
    const signature = arr.length
      ? `${arr.length}-${arr[0]?.rawDate ?? ''}-${arr[arr.length - 1]?.rawDate ?? ''}`
      : ''
    if (meetingsSignatureRef.current === signature) return
    meetingsSignatureRef.current = signature
    scrollToToday(containerRef, meetings, timezone)
  })

  // When a meeting is selected, scroll so the full card is visible (below the header, not cut off at bottom)
  useEffect(() => {
    if (!selectedMeetingId || typeof window === 'undefined') return undefined
    const id = selectedMeetingId
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollMeetingCardIntoView(id, containerRef))
    })
    return () => cancelAnimationFrame(rafId)
  }, [selectedMeetingId])

  const handleLoadMoreTop = useCallback(() => {
    if (isPreviousLoading) return
    // track from Contact page
    if (contactUuid) {
      trackContactInteraction({
        track,
        location: 'interactions_feed_all',
        action: 'view_more_interactions',
        contactUuid
      })
    }
    if (typeof window === 'undefined') return

    setScrollData({
      height: document.documentElement.scrollHeight,
      top: window.scrollY || document.documentElement.scrollTop
    })

    dataLoadedRef.current = true
    onLoadPrevious()
  }, [isPreviousLoading, onLoadPrevious, setScrollData, track, contactUuid])

  const handleLoadMoreBottom = useCallback(() => {
    if (isNextLoading) return

    dataLoadedRef.current = true
    onLoadNext()
  }, [isNextLoading, onLoadNext])

  const eventUuids = useMemo(() => (Array.isArray(meetings)
    ? meetings.flatMap((group) => group.events?.map((e) => e.uuid).filter(Boolean) || [])
    : []), [meetings])
  const packageNameMap = usePackageNameMap(eventUuids)

  const {
    hasFutureEvents,
    hasMeetingInProgress
  } = useMemo(() => getMeetingsMetadata(meetings, timezone), [meetings, timezone])

  const firstVisibleMIEvent = useMemo(() => {
    if (!meetings || !Array.isArray(meetings)) {
      return undefined
    }

    const allEvents = meetings.flatMap((group) => group.events || [])
    return allEvents.find((event) => !event.past
      && !event.cancelled
      && event.meetingIntelligence
      && ['SCHEDULED'].includes(event.meetingIntelligence.status)
    )
  }, [meetings])

  return (
    <Container>
      <div ref={containerRef}>
        {onLoadPrevious && (
          <Bookend
            onClick={handleLoadMoreTop}
            isLoading={isPreviousLoading}
            hasMore={hasPrevious}
          />
        )}
        {meetingsArray.map((meetingGroup) => (
          <MeetingGroup
            key={meetingGroup.date}
            date={meetingGroup.date}
            rawDate={meetingGroup.rawDate}
            events={meetingGroup.events || []}
            showBuffers={showBuffers}
            selectedMeetingId={selectedMeetingId}
            anyInProgress={hasMeetingInProgress}
            firstVisibleMIEvent={firstVisibleMIEvent}
            trackFromContactPage={!!contactUuid}
            packageNameMap={packageNameMap}
            onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback}
            onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback}
            firstUpcomingEventKey={firstUpcomingEventKey}
            registerDateGroupRef={onVisibleDateRangeChange ? registerDateGroupRef : undefined}
          />
        ))}
        <Bookend
          onClick={handleLoadMoreBottom}
          isLoading={isNextLoading}
          hasMore={hasNext}
          anyFutureMeetings={hasFutureEvents}
          isUpcoming
        />
        <BottomPadding />
      </div>
      <ScrollToTodayButton
        containerRef={containerRef}
        meetings={meetings}
        timezone={timezone}
        contactUuid={contactUuid}
      />
    </Container>
  )
}
